/* globals zoomSdk */
import { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Divider } from '@zoom/react-ui';
import { IconPlusFill, IconCloseSmallV2, IconPlay } from '@zoom/icons-react';
import '../root/App.css';
import './Queue.css';
import SetTimer from './SetTimer';
import CustomTooltip from '../utilities/CustomTooltip/CustomTooltip';
import { TimeButtons } from './Buttons';
import { SoundButton } from '../utilities/MusicInTimer/SoundButton';
import { formatTime } from '../utils';
import { debounce } from 'lodash';
import { selectedTimerMusicOption } from '../utilities/MusicInTimer/reducer';
import { setMusicSelectedOption } from '../utilities/MusicInTimer/actions';

export default function Queue({
  countdownKey,
  countdownStatus,
  startTime,
  timerIsRunning,
  timerTime,
  setTimerTimeWithConstraint,
  stopwatchIsOn,
  openModal,
  setOpenModal,
  supportRunningContext,
  myTimerAudioToggleOn,
  dynamicIndicatorClientCheck,
  clientSupportNegativeAlarm,
  setTimerTime,
  startTimer,
  onContinueModal,
  isMacOS,
  setStartTime,
  setPresetTime,
  refsList,
  oldHostSupport,
  timerQueue,
  setTimerQueue,
  isTimerDISet,
  myDynamicIndicator,
  isStopwatchDISet,
  stopwatchVideoToggleOn,
  timerVideoToggleOn,
}) {
  const [modal, setModal] = useState({});
  const [tempTimerTime, setTempTimerTime] = useState(
    parseInt(window.localStorage.getItem('LastSetTime')) || 300
  );
  const [isScrollable, setIsScrollable] = useState(false);
  const queueRef = useRef(null);
  const [gotMessage, setGotMessage] = useState(false);
  const selectedOption = useSelector(selectedTimerMusicOption);
  const [currentAlarmToneSetting, setCurrentAlarmToneSetting] = useState({
    name: selectedOption?.name,
    id: selectedOption?.id,
  });
  const dispatch = useDispatch();

  const hideControls =
    (isTimerDISet || isStopwatchDISet) && !myDynamicIndicator;

  const formatTimer = useCallback(
    (time) => {
      let hours = formatTime(time).hours;
      let minutes = formatTime(time).minutes;
      let seconds = formatTime(time).seconds;
      if (time === 0) {
        if (!timerIsRunning) {
          hours = '00';
          minutes = '00';
          seconds = '00';
        }
      }
      return { hours: hours, minutes: minutes, seconds: seconds };
    },
    [
      countdownKey,
      countdownStatus,
      startTime,
      timerIsRunning,
      timerTime,
      setTimerTimeWithConstraint,
      stopwatchIsOn,
      openModal,
      supportRunningContext,
      myTimerAudioToggleOn,
      dynamicIndicatorClientCheck,
      tempTimerTime,
    ]
  );

  const addToQueue = (time) => {
    setTimerQueue((queue) => [
      ...queue,
      [time, currentAlarmToneSetting?.name, currentAlarmToneSetting?.id],
    ]);
    setModal((prevState) => ({ ...prevState, open: false }));
  };

  const startTimerWithValue = (index, timerData) => {
    const [time, name, id] = timerData;
    if (stopwatchIsOn && stopwatchVideoToggleOn && timerVideoToggleOn) {
      setOpenModal({ open: true, time, index });
    } else {
      window.localStorage.setItem(
        'preferredMusic',
        JSON.stringify({ name: name, id: id })
      );
      dispatch(setMusicSelectedOption({ name: name, id: id }));

      setTimerQueue((queue) => queue.filter((_, id) => id !== index));
      setTimeout(() => {
        startTimer(time);
      }, 0);
    }
  };

  const editTimerQueue = (index, timerData) => {
    const [time, name, id] = timerData;
    setModal({ mode: 'edit', open: true, id: index });
    setTempTimerTime(time);
    setCurrentAlarmToneSetting({ name, id });
  };

  const updateTimerQueue = (index) => {
    setModal({ open: false });
    const newTimerQueue = [...timerQueue];
    newTimerQueue[index] = [
      tempTimerTime,
      currentAlarmToneSetting?.name,
      currentAlarmToneSetting?.id,
    ];
    setTimerQueue(newTimerQueue);
  };

  useEffect(() => {
    const checkIfScrollable = () => {
      if (queueRef.current) {
        const { scrollHeight, clientHeight } = queueRef.current;
        setIsScrollable(scrollHeight > clientHeight);
      }
    };

    const debouncedCheck = debounce(() => {
      requestAnimationFrame(checkIfScrollable);
    }, 100);

    const resizeObserver = new ResizeObserver(debouncedCheck);

    if (queueRef.current) {
      resizeObserver.observe(queueRef.current);
    }

    return () => {
      if (resizeObserver && queueRef.current) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (isTimerDISet && !myDynamicIndicator) {
      if (timerQueue.length === 0 && gotMessage === false) {
        zoomSdk.sendMessage({
          payload: {
            newUserJoined: true,
          },
        });
      }
    }
  }, [isTimerDISet, myDynamicIndicator, timerQueue, gotMessage]);

  useEffect(() => {
    if (isTimerDISet && !myDynamicIndicator) {
      const onMessageHandler = (data) => {
        if (data.payload.timers) {
          setTimerQueue(data.payload.timers);
          setGotMessage(true);
        }
      };

      zoomSdk.addEventListener('onMessage', onMessageHandler);

      return () => {
        zoomSdk.removeEventListener('onMessage', onMessageHandler);
      };
    }
  }, [isTimerDISet, myDynamicIndicator]);

  useEffect(() => {
    if (isTimerDISet && myDynamicIndicator) {
      const sendQueueData = () => {
        zoomSdk.sendMessage({
          payload: {
            timers: timerQueue,
          },
        });
      };
      const onMessageHandler = (data) => {
        if (data.payload.newUserJoined === true) {
          sendQueueData();
        }
      };

      sendQueueData();
      zoomSdk.addEventListener('onMessage', onMessageHandler);

      return () => {
        zoomSdk.removeEventListener('onMessage', onMessageHandler);
      };
    }
  }, [myDynamicIndicator, myDynamicIndicator, timerQueue]);

  useEffect(() => {
    if (!isTimerDISet && !myDynamicIndicator && gotMessage) {
      setTimerQueue([]);
    }
  }, [isTimerDISet, myDynamicIndicator, gotMessage]);

  return (
    <div className='Home__timer_queue'>
      <div
        className={`Home__timer_queue_header${
          isScrollable ? ' scrollable' : ''
        }`}
      >
        <span id='Home__timer_queue_header_text'>Up next</span>
        {!hideControls && (
          <Button
            variant='icon'
            type='tertiary'
            onClick={() => setModal({ mode: 'add', open: true })}
          >
            <IconPlusFill />
          </Button>
        )}
      </div>
      <div
        className={`Home__timer_queue_container${
          supportRunningContext ? '' : ' full'
        }`}
        ref={queueRef}
      >
        <ul>
          {timerQueue.map((timerData, index) => {
            const [time] = timerData;
            return (
              <li key={index}>
                <div className='Home__timer_queue_item'>
                  <div
                    className='Home__timer_queue_time'
                    onClick={() => editTimerQueue(index, timerData)}
                  >
                    {Math.abs(formatTimer(time).hours) > 0 && (
                      <>
                        <span>{formatTimer(time).hours}</span>
                        <span>:</span>
                      </>
                    )}
                    <span>{formatTimer(time).minutes} </span>
                    <span>:</span>
                    <span>{formatTimer(time).seconds}</span>
                  </div>
                  {!hideControls && (
                    <div className='Countdown__control_container'>
                      <div className='Countdown__control_button'>
                        <Button
                          variant='icon'
                          type='tertiary'
                          aria-label='Start'
                          onClick={() => startTimerWithValue(index, timerData)}
                        >
                          <IconPlay />
                        </Button>
                      </div>
                      <Button
                        variant='icon'
                        type='tertiary'
                        data-tooltip-id='timer-cancelbutton-tooltip'
                        data-tooltip-delay-show={300}
                        onClick={() =>
                          setTimerQueue((queue) =>
                            queue.filter((_, id) => id !== index)
                          )
                        }
                      >
                        <IconCloseSmallV2 />
                      </Button>
                    </div>
                  )}
                </div>
                {timerQueue.length >= 1 && <Divider />}
              </li>
            );
          })}
        </ul>
      </div>
      {modal.open && (
        <div className='Home__set_timer_modal'>
          <Button
            id='close'
            variant='icon'
            type='tertiary'
            aria-label='close'
            onClick={() =>
              setModal((prevState) => ({ ...prevState, open: false }))
            }
          >
            <IconCloseSmallV2 />
          </Button>
          <div className='Home__set_timer_container_in_modal'>
            <div
              data-testid='timerContainer'
              className={`Home__timer_input_container${
                (!clientSupportNegativeAlarm || !supportRunningContext) &&
                timerTime === 0 &&
                timerIsRunning
                  ? ' fade'
                  : ''
              }`}
            >
              <SetTimer
                hours={formatTimer(tempTimerTime).hours}
                minutes={formatTimer(tempTimerTime).minutes}
                seconds={formatTimer(tempTimerTime).seconds}
                timerTime={timerTime}
                setTimerTime={setTimerTime}
                setTimerTimeWithConstraint={setTimerTimeWithConstraint}
                startTimerOnEnterPress={startTimer}
                stopwatchIsOn={stopwatchIsOn}
                openModal={openModal}
                onContinueModal={onContinueModal}
                supportRunningContext={supportRunningContext}
                timerVideoToggleOn={myTimerAudioToggleOn}
                dynamicIndicatorClientCheck={dynamicIndicatorClientCheck}
                tempTimerTime={tempTimerTime}
                setTempTimerTime={setTempTimerTime}
              />
            </div>
            <div className='Home__timer_controls'>
              <div className='Home__start_button'>
                <Button
                  size='large'
                  fullWidth={true}
                  id={
                    tempTimerTime === 0
                      ? 'input_start_button_disabled'
                      : 'input_start_button'
                  }
                  data-tooltip-id='timer-startbutton-tooltip'
                  data-tooltip-delay-show={300}
                  disabled={tempTimerTime === 0}
                  onClick={() => {
                    modal.mode === 'add'
                      ? addToQueue(tempTimerTime)
                      : updateTimerQueue(modal.id);
                  }}
                >
                  Save
                </Button>
                <CustomTooltip
                  text='Keyboard shortcut'
                  keyName={isMacOS ? 'return' : 'enter'}
                  id='timer-startbutton-tooltip'
                />
              </div>
              <TimeButtons
                setStartTime={setStartTime}
                setPresetTime={setPresetTime}
                setTimerTimeWithConstraint={setTimerTimeWithConstraint}
                setTempTimerTime={setTempTimerTime}
                mode='add'
                addToQueue={addToQueue}
              />
              <SoundButton
                refs={refsList}
                source='Queue'
                currentAlarmToneSetting={currentAlarmToneSetting}
                setCurrentAlarmToneSetting={setCurrentAlarmToneSetting}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
